import { GridReadyEvent, RowSelectedEvent } from "@ag-grid-community/core";
import { Component, OnInit, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute, Router } from "@angular/router";
import { PoManufacturerBackorder } from "app/model/entities/-po-backorder";

import { AgFns, GridOptionsExt, ISortModel } from "app/shared/ag-fns";

import { takeUntil } from "rxjs/operators";
import { DomainBaseComponent } from "../shared/domain-base.component";
import { DomainService } from "../shared/domain.service";
import { DateFns } from "@/shared/date-fns";
import { EntityEvent } from "@/model/entities/entity-event";
import { EntityFns } from "@/shared/entity-fns";
import { EntityEventTypeEnum } from "@/model/entities/enums";
import { JoDetail } from "@/model/entities/jo-detail";
import { AuthService } from "@/shared/auth.service";

@Component({
  selector: "app-manuf-backorders",
  templateUrl: "./manuf-backorders.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ManufBackordersComponent extends DomainBaseComponent implements OnInit {
  pombGridOptions: GridOptionsExt;
  pombItems: PoManufacturerBackorder[];
  shouldShowIdColumns = true;

  manufacturerGuid: string;
  currentPomb: PoManufacturerBackorder;

  podToPombMap = new Map<number, PoManufacturerBackorder>();

  // True if accessed via anonymous link (e.g., /manufacturer-backorders/:guid)
  isAnonymousAccess = false;

  constructor(protected domainService: DomainService, protected route: ActivatedRoute, router: Router, private authService: AuthService) {
    super(domainService);

    // Determine if this is anonymous access by checking the URL path
    this.isAnonymousAccess = this.router.url.startsWith("/manufacturer-backorders");

    this.route.queryParamMap.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.updateFromContext();
    });
  }

  closeWindow() {
    window.close();
  }

  async updateFromContext() {
    this.manufacturerGuid = this.route.snapshot.params["guid"];

    this.pombGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onPombGridReady,
      onRowSelected: this.onRowSelected,
      onCellValueChanged: this.onCellValueChanged,
      getRowId: (p) => p.data?.poDetailId.toString(),

      rowGroupPanelShow: "always",
      stopEditingWhenCellsLoseFocus: true,
      suppressDragLeaveHidesColumns: false,
      suppressMakeColumnVisibleAfterUnGroup: false,
    });
    AgFns.captureGridRouteParams(this.pombGridOptions, this.route, "manufGuid");

    await this.requery();
    this.isPageReady = true;
  }

  async requery() {
    var pombs: PoManufacturerBackorder[];
    try {
      pombs = <PoManufacturerBackorder[]>(<any>await this.dbQueryService.getPoDetailManufacturerBackorders(this.manufacturerGuid));
    } catch (e) {
      this.router.navigate(["/login"], { skipLocationChange: false });
      return;
    }

    // group the rows and sum the quantities

    // this.podToPombMap.clear();
    // const displayItems = _.sortBy(this.podItems, this.calcKey);
    const itemMap = {};
    // each pomb will actually represents multiple pods with the same calcKey ( see below ) value.
    pombs.forEach((pomb) => {
      const key = this.calcKey(pomb);
      if (itemMap[key]) {
        itemMap[key].orderQty += pomb.orderQty;
        itemMap[key].recievedQty += pomb.receivedQty;
        itemMap[key].canceledQty += pomb.canceledQty;
        itemMap[key]["podIds"].push(pomb.poDetailId);
      } else {
        pomb["podIds"] = [pomb.poDetailId];
        pomb._isChecked = false;
        itemMap[key] = pomb;
      }
      // this.podToPombMap.set(pomb.podId, pomb);
    });
    this.pombItems = Object.values(itemMap);
    this.currentPomb = null;

    this.pombGridOptions?.apiX?.refreshCells();
  }

  calcKey(item: PoManufacturerBackorder) {
    return (
      item.poDetail.poHeaderId.toString() +
      "/" +
      item.product.productType.style +
      item.product.featureChoicesExtract +
      item.poDetail.expectedShipDate
    );
  }

  async canDeactivate() {
    this.stopEditing();
    var ok = await super.canDeactivate();
    if (!ok) return false;
    if (this.uow.hasChanges()) {
      const ok = await this.dialogService.yesNoDialog("Changes have not been saved", "Do you want to save before exiting?");
      if (ok) {
        await this.saveChanges();
      }
    }
    return true;
  }

  // This method is called AFTER ngAfterViewInit - this is important
  // because the ColDef's here reference TemplateRef's above that will not be resolved until here.
  onPombGridReady(event: GridReadyEvent) {
    const colDefs = [
      // Not sure what this is for.
      // { ...AgFns.createCheckboxProps('Checked', '_isChecked'), editable: true  },
      {
        headerName: "Purchase Order",
        field: "poDetail.poHeaderId",
        filter: "agNumberColumnFilter",
        cellClass: "ag-left-aligned-cell",
        headerClass: "ag-left-aligned-cell",
        enableRowGroup: true,
        maxWidth: 160,
      },
      {
        headerName: "SKU",
        field: "product.productType.style",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Features",
        field: "product.featureChoicesExtract",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Description",
        field: "product.productType.description",
        filter: "agTextColumnFilter",
        enableRowGroup: true,
      },
      {
        headerName: "Order Qty",
        field: "orderQty",
        filter: "agNumberColumnFilter",
      },
      { headerName: "Received Qty", field: "receivedQty", ...AgFns.fixWidth(120) },
      { headerName: "Canceled Qty", field: "canceledQty", ...AgFns.flexWidth(120) },
      {
        headerName: "Manufacturer",
        children: [
          {
            headerName: "Expected Ship Date",
            field: "poDetail.expectedShipDate",
            editable: true,
            cellClass: "cell-editable",
            filter: "agDateColumnFilter",
            cellEditor: "agDateCellEditor",
          },
          {
            headerName: "Last Updated Ts",
            field: "poDetail.expectedShipDateModTs",
            filter: "agDateColumnFilter",
          },
        ],
      },
      // { headerName: 'Manufacturer Expected Date', field: 'poDetail.expectedShipDate', editable: true,
      //   cellStyle: AgStyleFns.setEditableStyle, filter: 'agDateColumnFilter', cellEditor: DateCellEditorPngComponent },
      // { headerName: 'Manuf Last Updated Ts', field: 'poDetail.expectedShipDateModTs', filter: 'agDateColumnFilter' },
    ];

    const sortModel = [
      { colId: "poDetail.poHeaderId", sort: "asc" },
      { colId: "product.productType.style", sort: "asc" },
    ] as ISortModel;

    // next line is needed by the buildDatasource method
    AgFns.initGrid(event, colDefs, sortModel);
    AgFns.applyGridRouteParams(this.pombGridOptions);
  }

  updateLocation(key: any = null) {
    const urlTree = this.router.createUrlTree(["/manuf-backorders"]);
    const url = AgFns.buildGridRouteParamsUrl(urlTree, this.pombGridOptions, key && key.toString());
    this.domainService.location.replaceState(url);
  }

  getTitle() {
    if (!this.pombItems?.length) {
      return;
    }
    return this.pombItems[0].product.productType.manufacturer.name;
  }

  stopEditing() {
    this.pombGridOptions.apiX?.stopEditing();
  }

  async onRowSelected(e: RowSelectedEvent) {
    // check if a deselect event and ignore
    if (!e.node.isSelected()) {
      return;
    }
    const pomb = e.data as PoManufacturerBackorder;
    if (!pomb) {
      return;
    }
    this.currentPomb = pomb;
  }

  async onCellValueChanged(event) {
    const colId = event?.column?.colId;
    if (colId != "poDetail.expectedShipDate") return;
    const poDetail = this.currentPomb.poDetail;
    poDetail.expectedReceiveDate = DateFns.dateAdd(poDetail.expectedShipDate, poDetail.poHeader.manufacturer.transitTimeDays, "days");
    if (poDetail.joDetail && poDetail.joDetail.expectedShipDate != poDetail.expectedReceiveDate) {
      poDetail.joDetail.expectedShipDate = poDetail.expectedReceiveDate;
      this.dbSaveService.createEntityEvent({
        entityTypeName: "JoDetail",
        entityId: poDetail.joDetail.id.toString(),
        entityEventTypeId: EntityEventTypeEnum.JoDetailExpectedShipDateChange,
      });
    }
    poDetail.expectedShipDateModTs = new Date(Date.now());
  }

  async saveChanges() {
    // Use anonymous endpoint since manufacturers access this page without authentication.
    // The server refuses the save unless it carries the same Guid as the link.
    const sr = await this.dbSaveService.saveChangesAnonymous(`SaveManufacturerBackorders?guid=${encodeURIComponent(this.manufacturerGuid)}`);
    this.pombGridOptions?.apiX?.refreshCells();
    this.toastr.success(`${sr.entities.length} records(s) saved`);
  }

  async cancelChanges() {
    this.dbSaveService.rejectChanges();
    await this.requery();
  }
}
